import React from "react";
import moment from "moment";
import { Badge, Button } from "../ui";
import Avatar from "./Avatar";
import {
  attributionParts,
  AUTOMATIC_PILL,
  CLIENT_VIEWER,
  linkifyParts,
  messageInitials,
  messageRole,
} from "./threadFormat";

// One transcript row (thread transcript spec § One message row): the
// role-coloured square, then the attribution line — its name bold in the role
// colour, its rest muted, one element holding the whole line — and the time,
// with the text, attachments and a failed send's line beneath.
export default function MessageBubble({
  message,
  onRetry,
  viewer = CLIENT_VIEWER,
}) {
  const role = messageRole(message, viewer);
  const { name, rest } = attributionParts(message, viewer);
  const welcome = message.kind === "welcome";
  return (
    <div
      data-slot="thread-msg"
      data-role={role}
      className="tw-flex tw-items-start tw-gap-3"
    >
      <Avatar initials={messageInitials(message)} role={role} />
      <div
        data-slot="thread-msg-body"
        className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1"
      >
        <div
          data-slot="thread-msg-meta"
          className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-2"
        >
          <span
            data-slot="thread-msg-who"
            className="tw-text-sm tw-text-text"
          >
            <b data-slot="thread-msg-name" className="tw-font-bold">
              {name}
            </b>
            {rest && (
              <span
                data-slot="thread-msg-rest"
                className="tw-font-regular tw-text-text-muted"
              >
                {rest}
              </span>
            )}
          </span>
          {welcome && (
            <Badge tone="neutral" data-slot="thread-pill">
              {AUTOMATIC_PILL}
            </Badge>
          )}
          <span
            data-slot="thread-msg-time"
            className="tw-text-xs tw-text-text-muted"
          >
            {moment(message.createdAt).format("h:mm A")}
          </span>
        </div>
        {message.content?.text && (
          <p
            data-slot="thread-msg-text"
            className="tw-m-0 tw-whitespace-pre-wrap tw-break-words tw-text-sm tw-text-text"
          >
            {linkifyParts(message.content.text).map((part, i) =>
              part.type === "link" ? (
                <a
                  key={i}
                  className="tw-text-link"
                  href={part.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {part.value}
                </a>
              ) : (
                part.value
              ),
            )}
          </p>
        )}
        {(message.content?.attachments || []).map((a, i) => (
          // The index is part of the key because an optimistic message uses the
          // filename as its documentId (ThreadPage.jsx), so attaching two
          // identically-named files would otherwise produce duplicate keys.
          <div
            key={`${message._id}-${a.documentId}-${i}`}
            data-slot="thread-attachment"
            className="tw-flex tw-gap-2 tw-text-sm tw-text-text"
          >
            {a.url ? (
              <a
                className="tw-text-link"
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {a.filename}
              </a>
            ) : (
              <span>{a.filename}</span>
            )}
            <span
              data-slot="thread-attachment-size"
              className="tw-text-text-muted"
            >
              {Math.max(1, Math.round((a.size || 0) / 1024))} KB
            </span>
          </div>
        ))}
        {message.failed && (
          <div
            data-slot="thread-msg-failed"
            className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-warning"
          >
            <span>Couldn't send. Try again.</span>
            <Button
              variant="text"
              size="sm"
              onClick={() => onRetry(message)}
            >
              Try again
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
